import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import Toast, { ToastStack } from '../components/ui/Toast';

/**
 * useToast() — branded replacement for window.alert().
 *
 * Returns a callable object:
 *   const toast = useToast();
 *   toast.error('Could not save: ' + err.message);   // red variant
 *   toast.success('Document approved');               // green variant
 *   toast.info('Heads up');                           // blue variant
 *   toast({ variant: 'error', title: 'Upload failed', message: '…' });  // full options
 *
 * Each toast auto-dismisses after ~4.5s by default; pass `duration: 0` for
 * sticky toasts that only close on the X button.
 *
 * Mechanical swap from window.alert:
 *   // Before
 *   alert('Could not save: ' + err.message);
 *   // After
 *   toast.error('Could not save: ' + err.message);
 */

const ToastCtx = createContext(null);

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  // Stable seen-ids set so we never replay a dismissed id (defensive).
  const dismissedRef = useRef(new Set());

  const dismiss = useCallback((id) => {
    if (dismissedRef.current.has(id)) return;
    dismissedRef.current.add(id);
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((opts) => {
    const id = nextId++;
    const item = {
      id,
      variant: opts?.variant || 'error',
      title: opts?.title,
      message: opts?.message,
      duration: opts?.duration ?? 4500,
    };
    setToasts((list) => [...list, item]);
    return id;
  }, []);

  // The returned hook value is a callable+object. Calling it = generic toast;
  // .error / .success / .info shortcut to the right variant. Each shortcut
  // also accepts either a string (used as `message`) or a full options object.
  const toast = useMemo(() => {
    const fn = (opts) => push(typeof opts === 'string' ? { message: opts } : opts);
    const shortcut = (variant) => (arg) => {
      if (typeof arg === 'string') return push({ variant, message: arg });
      return push({ ...arg, variant });
    };
    fn.error   = shortcut('error');
    fn.success = shortcut('success');
    fn.info    = shortcut('info');
    fn.dismiss = dismiss;
    return fn;
  }, [push, dismiss]);

  return (
    <ToastCtx.Provider value={toast}>
      {children}
      {toasts.length > 0 && (
        <ToastStack>
          {toasts.map((t) => (
            <Toast
              key={t.id}
              variant={t.variant}
              title={t.title}
              message={t.message}
              duration={t.duration}
              onDismiss={() => dismiss(t.id)}
            />
          ))}
        </ToastStack>
      )}
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) {
    throw new Error('useToast() must be called inside <ToastProvider>');
  }
  return ctx;
}
